import { useAppDispatch, useAppSelector } from '@/state';
import { setSelectedRelease } from '@/state/slices';
import { CoreApi, ProjectsApi, ReleasesApi } from '@api';
import {
  Box,
  Button,
  Card,
  FileUploadFileMimeType,
  Heading,
  Separator,
  Span,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  FormComboBox,
  FormController,
  FormFileUpload,
  FormInput,
  formSchemas,
  RenderFormElement,
} from '@components/form';
import { CoreType, ReleasesType } from '@types';
import {
  FileUpload,
  OverlayDialog,
  SelectWithAction,
  Spinner,
  toasterApi,
} from '@ui';
import { useMemo, useState } from 'react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { z } from 'zod';

interface Props {
  defaultRelease?: Pick<
    ReleasesType.Release,
    'id' | 'number' | 'project' | 'due_date'
  >;
  isNew?: boolean;
  replacementFor?: ReleasesType.Release;
}

type PresignedUploadItem = {
  filename: string;
  upload_url: string;
  file_url: string;
};

const buildUploadFilename = (kind: 'bom' | 'frame_qty', file: File) => {
  const timestamp = Date.now();
  const safeName = file.name.replace(/\s+/g, '_');
  return `release_${kind}_${timestamp}_${safeName}`;
};

const uploadFileToPresignedUrl = async (
  file: File,
  presigned: PresignedUploadItem,
) => {
  const response = await fetch(presigned.upload_url, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload ${file.name} to S3`);
  }

  return presigned.file_url;
};

const Inner_ManageCard: React.FC<Props> = ({
  defaultRelease,
  isNew,
  replacementFor,
}: Props) => {
  const formSchema = replacementFor
    ? replacementReleaseSchema
    : isNew
      ? releaseSchema
      : releaseSchemaPartial;
  type FormData = z.infer<typeof formSchema>;

  const [postRelease, { isLoading: isPostReleaseLoading }] =
    ReleasesApi.Api.usePostReleaseMutation();
  const [patchRelease, { isLoading: isPatchReleaseLoading }] =
    ReleasesApi.Api.usePatchReleaseMutation();
  const [postAttachmentsPresignedUrls, { isLoading: isPresigningUploads }] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  const { data: getAllProjects } = ProjectsApi.Api.useGetAllProjectsQuery();
  const { data: bomMappings } = ReleasesApi.Api.useGetBomMappingsQuery({
    mapping_type: ReleasesType.bomMappingType.Part,
  });
  const { data: frameBomMappings } = ReleasesApi.Api.useGetBomMappingsQuery({
    mapping_type: ReleasesType.bomMappingType.Frame,
  });

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );

  const dispatch = useAppDispatch();

  const createFormDefaultValues = useMemo(
    () => ({
      number: isNew ? undefined : defaultRelease?.number || '',
      project: isNew
        ? (replacementFor?.project ?? selectedProject?.id!)
        : defaultRelease?.project || 0,
      due_date: isNew ? '' : defaultRelease?.due_date || '',
      replacement_reason: replacementFor ? '' : undefined,
    }),
    [
      isNew,
      defaultRelease?.number,
      defaultRelease?.project,
      defaultRelease?.due_date,
      selectedProject?.id,
      replacementFor?.id,
      replacementFor?.project,
    ],
  );

  const uploadReleaseFilesAndGetUrls = async ({
    bomFile,
    frameFile,
  }: {
    bomFile: File;
    frameFile?: File;
  }) => {
    const presignPayload: CoreType.PostAttachmentsPresignedUrls['body'] = [];

    const bomUploadFilename = buildUploadFilename('bom', bomFile);
    presignPayload.push({
      module: 'bom',
      filename: bomUploadFilename,
      content_type: bomFile.type || 'application/octet-stream',
      size: bomFile.size,
    });

    const frameUploadFilename = frameFile
      ? buildUploadFilename('frame_qty', frameFile)
      : null;
    if (frameFile && frameUploadFilename) {
      presignPayload.push({
        module: 'frame_qty',
        filename: frameUploadFilename,
        content_type: frameFile.type || 'application/octet-stream',
        size: frameFile.size,
      });
    }

    const presignedUrls = await postAttachmentsPresignedUrls({
      body: presignPayload,
    }).unwrap();

    const bomPresigned = presignedUrls?.find(
      (item) => item.filename === bomUploadFilename,
    );

    if (!bomPresigned) {
      throw new Error('Failed to get presigned upload URL for BOM file');
    }

    const bomFileUrl = await uploadFileToPresignedUrl(bomFile, bomPresigned);

    let frameFileUrl: string | undefined;
    if (frameFile && frameUploadFilename) {
      const framePresigned = presignedUrls?.find(
        (item) => item.filename === frameUploadFilename,
      );
      if (!framePresigned) {
        throw new Error('Failed to get presigned upload URL for Frame BOM file');
      }
      frameFileUrl = await uploadFileToPresignedUrl(frameFile, framePresigned);
    }

    return {
      bomFileUrl,
      frameFileUrl,
    };
  };

  const onFormSubmit = async (formData: FormData) => {
    OverlayDialog.open('confirmReleaseUpdate', {
      overlayId: 'confirmReleaseUpdate',
      title: `Confirm ${
        replacementFor
          ? 'BOM Replacement'
          : isNew
            ? 'Release Creation'
            : 'Release Update'
      }`,
      description: `Are you sure you want to ${
        replacementFor
          ? 'upload a replacement for'
          : isNew
            ? 'create'
            : 'update'
      } the release?`,
      content: (
        <VStack align="flex-start" gap={2}>
          {!isNew && (
            <Text>
              Release:{' '}
              <Span
                textStyle={'heading.card'}
                color={'tertiary.fg'}
              >{`${defaultRelease?.number}`}</Span>
            </Text>
          )}
          {replacementFor && (
            <Text color="fg.warning">
              Current BOM: {replacementFor.number}; Project ID{' '}
              {replacementFor.project}. It remains active unless this
              replacement parses successfully.
            </Text>
          )}
          <RenderFormElement
            previousValue={
              isNew ? undefined : defaultRelease?.number || undefined
            }
            currentValue={formData.number}
            label="Release Number"
          />
          <RenderFormElement
            previousValue={
              isNew
                ? undefined
                : (getAllProjects?.find((p) => p.id === defaultRelease?.project)
                    ?.name ?? '')
            }
            currentValue={
              getAllProjects?.find((p) => p.id === formData.project)?.name ?? ''
            }
            label="Project Name"
          />
        </VStack>
      ),
      handleConfirm: async () => {
        try {
          if (isNew) {
            if (
              !formData.project ||
              !formData.number ||
              !formData.bom_mapping ||
              !formData.bom_file
            ) {
              toasterApi({
                type: 'error',
                status: 400,
                code: 'Validation Error',
                message: 'Please complete all required fields',
              });
              return;
            }

            // A Frame BOM file requires a saved Frame BOM mapping to parse it.
            if (formData.frame_qty_file && !formData.frame_bom_mapping) {
              toasterApi({
                type: 'error',
                status: 400,
                code: 'Validation Error',
                message:
                  'Please select a Frame BOM mapping before uploading this frame quantity file.',
              });
              return;
            }

            const { bomFileUrl, frameFileUrl } =
              await uploadReleaseFilesAndGetUrls({
                bomFile: formData.bom_file,
                frameFile: formData.frame_qty_file,
              });

            const releasePayload: any = {
              project: formData.project,
              number: formData.number,
              bom_mapping: formData.bom_mapping,
              bom_file: bomFileUrl,
            };
            if (replacementFor) {
              releasePayload.replaces_release = replacementFor.id;
              releasePayload.replacement_reason =
                formData.replacement_reason;
            }

            // Attach the Frame BOM file + selected saved Frame BOM mapping; the
            // backend snapshots the mapping onto the release.
            if (frameFileUrl && formData.frame_bom_mapping) {
              releasePayload.frame_qty_file = frameFileUrl;
              releasePayload.frame_bom_mapping = formData.frame_bom_mapping;
            }

            if (formData.due_date) {
              releasePayload.due_date = formData.due_date;
            }

            const createdRelease = await postRelease(releasePayload).unwrap();

            dispatch(
              setSelectedRelease({
                id: createdRelease.id,
                item: createdRelease,
              }),
            );

            toasterApi({
              type: 'success',
              status: 201,
              code: 'Success',
              message: replacementFor
                ? 'Replacement BOM uploaded for validation'
                : 'Created Release',
            });

            return;
          }

          if (!defaultRelease?.id) {
            toasterApi({
              type: 'error',
              status: 400,
              code: 'Error',
              message: 'Release ID is missing',
            });
            return;
          }

          const patchBody: ReleasesApi.Args['patchRelease']['body'] = {
            project: formData.project!,
            number: formData.number!,
          };

          // Only include due_date in the PATCH body when the user actually
          // changed it. This keeps the propagation server-side a no-op
          // when the field is unchanged.
          const previousDueDate = defaultRelease?.due_date ?? null;
          const nextDueDate = formData.due_date || null;
          if (nextDueDate !== previousDueDate) {
            patchBody.due_date = nextDueDate;
          }

          const { data } = await patchRelease({
            releaseId: defaultRelease.id,
            body: patchBody,
          });

          if (data) {
            dispatch(
              setSelectedRelease({
                id: data.id,
                item: data,
              }),
            );
          }

          toasterApi({
            type: 'success',
            status: 200,
            code: 'Success',
            message: 'Updated Release',
          });
        } catch (e: any) {
          toasterApi({
            type: 'error',
            status: e?.status || 500,
            code: 'Error',
            message:
              e?.data?.detail ||
              e?.data?.error ||
              e?.message ||
              `Failed to ${isNew ? 'create' : 'update'} release`,
          });
        }
      },
    });
  };

  return (
    <Card.Root width="100%" variant="elevated">
      <Spinner
        isLoading={
          isPostReleaseLoading || isPatchReleaseLoading || isPresigningUploads
        }
        text="Uploading Release file(s)..."
      />

      <Card.Body gap="7">
        <FormController
          key={`${defaultRelease?.id}-${isNew}-form-${isPostReleaseLoading}-${isPatchReleaseLoading}-${isPresigningUploads}`}
          label={
            replacementFor
              ? `Upload replacement for ${replacementFor.number}`
              : isNew
                ? 'Create Release'
                : 'Update Release Details'
          }
          formSchema={formSchema}
          helperText={`Please fill out the form to ${
            isNew ? 'create a new release' : 'update the release details'
          }.`}
          onFormSubmit={onFormSubmit}
          defaultValues={createFormDefaultValues}
          buttonProps={{
            variant: 'outline',
            color: 'tertiary.fg',
            width: '100%',
            mt: '4',
            ...(isNew ? { 'data-testid': 'tutorial-release-create-submit' } : {}),
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<FormData>;
            control: Control<FormData>;
            errors: FieldErrors<FormData>;
          }) => {
            return (
              <>
                <FormInput
                  control={control}
                  label="Release Number"
                  id="number"
                  register={register}
                  errors={errors}
                  helperText={formSchemas.releaseSchema.nameHelper}
                  required={isNew}
                />

                <FormComboBox
                  register={register}
                  control={control}
                  errors={errors}
                  id="project"
                  label="Project"
                  keyForDisplay="name"
                  keyForValue="id"
                  items={getAllProjects ?? []}
                  required={isNew}
                  clearable={!isNew}
                  disabled={Boolean(replacementFor)}
                  {...(isNew ? { 'data-testid': 'tutorial-release-project-select' } : {})}
                />

                <Box mt={2}>
                  <Heading size="sm" color="secondary.fg">
                    Part BOM
                  </Heading>
                  <Text fontSize="xs" color="fg.subtle">
                    Choose the parts-list column mapping and upload the Part BOM
                    file. Parsed into Parts and Production Summary.
                  </Text>
                </Box>

                <FormComboBox
                  register={register}
                  control={control}
                  errors={errors}
                  id="bom_mapping"
                  label="BOM Mapping"
                  keyForDisplay="name"
                  keyForValue="id"
                  items={bomMappings ?? []}
                  required={isNew}
                  clearable={!isNew}
                  {...(isNew ? { 'data-testid': 'tutorial-bom-mapping-select' } : {})}
                />

                <FormFileUpload
                  register={register}
                  control={control}
                  errors={errors}
                  id="bom_file"
                  label="Part BOM File"
                  maxFiles={1}
                  inputProps={
                    isNew ? { 'data-testid': 'tutorial-part-bom-upload' } : undefined
                  }
                  accept={
                    [
                      'application/vnd.ms-excel',
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    ] as FileUploadFileMimeType[]
                  }
                  required={isNew}
                />

                {isNew && (
                  <>
                    <Separator />
                    <Box mt={2}>
                      <Heading size="sm" color="secondary.fg">
                        Frame BOM
                      </Heading>
                      <Text fontSize="xs" color="fg.subtle">
                        Optional. Select a saved Frame BOM mapping and upload the
                        Frame BOM (frame quantity) file. Imported frames appear in
                        Production Summary and on the Create Work Order page.
                      </Text>
                    </Box>

                    {!frameBomMappings?.length ? (
                      <Text fontSize="sm" color="fg.subtle">
                        No Frame BOM mappings found. Create one in BOM Mapping
                        {' \u2192 '}Frame BOM.
                      </Text>
                    ) : (
                      <FormComboBox
                        register={register}
                        control={control}
                        errors={errors}
                        id="frame_bom_mapping"
                        label="Frame BOM Mapping"
                        keyForDisplay="name"
                        keyForValue="id"
                        items={frameBomMappings ?? []}
                        required={false}
                        clearable
                        data-testid="tutorial-frame-bom-mapping-select"
                      />
                    )}

                    <FormFileUpload
                      register={register}
                      control={control}
                      errors={errors}
                      id="frame_qty_file"
                      label="Frame BOM File"
                      maxFiles={1}
                      inputProps={{ 'data-testid': 'tutorial-frame-bom-upload' }}
                      accept={
                        [
                          'application/vnd.ms-excel',
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        ] as FileUploadFileMimeType[]
                      }
                      required={false}
                    />
                  </>
                )}

                <Separator />

                {replacementFor && (
                  <FormInput
                    control={control}
                    label="Replacement reason"
                    id="replacement_reason"
                    register={register}
                    errors={errors}
                    helperText="Required for BOM audit history."
                    required
                  />
                )}

                <FormInput
                  control={control}
                  label="Release Due Date"
                  id="due_date"
                  register={register}
                  errors={errors}
                  type="date"
                  helperText="Optional default. Apply it to existing work orders from the Manage Work Orders page."
                  required={false}
                />
              </>
            );
          }}
        </FormController>
      </Card.Body>
    </Card.Root>
  );
};

const ReimportFrameBomCard: React.FC<{ releaseId: number }> = ({
  releaseId,
}) => {
  const [frameFile, setFrameFile] = useState<File | undefined>(undefined);
  const [selectedMappingId, setSelectedMappingId] = useState<number | null>(
    null,
  );

  const { data: frameBomMappings } = ReleasesApi.Api.useGetBomMappingsQuery({
    mapping_type: ReleasesType.bomMappingType.Frame,
  });
  const [reprocessReleaseFrames, { isLoading }] =
    ReleasesApi.Api.useReprocessReleaseFramesMutation();
  const [postAttachmentsPresignedUrls, { isLoading: isPresigning }] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  const selectedMapping = frameBomMappings?.find(
    (m) => m.id === selectedMappingId,
  );

  const canReimport = Boolean(frameFile && selectedMappingId);

  const handleReimport = () => {
    if (!frameFile || !selectedMappingId) {
      toasterApi({
        type: 'error',
        status: 400,
        code: 'Validation Error',
        message:
          'Please select a Frame BOM mapping before uploading this frame quantity file.',
      });
      return;
    }

    OverlayDialog.open('confirmFrameReimport', {
      overlayId: 'confirmFrameReimport',
      title: 'Re-import Frame BOM',
      description:
        'This re-parses the uploaded frame file and updates frames for this release. Frames already assigned to a work order keep their quantity.',
      content: <Text>Proceed with the frame re-import?</Text>,
      handleConfirm: async () => {
        try {
          const uploadFilename = buildUploadFilename('frame_qty', frameFile);
          const presignedUrls = await postAttachmentsPresignedUrls({
            body: [
              {
                module: 'frame_qty',
                filename: uploadFilename,
                content_type: frameFile.type || 'application/octet-stream',
                size: frameFile.size,
              },
            ],
          }).unwrap();

          const presigned = presignedUrls?.find(
            (item) => item.filename === uploadFilename,
          );
          if (!presigned) {
            throw new Error(
              'Failed to get presigned upload URL for Frame BOM file',
            );
          }

          const fileUrl = await uploadFileToPresignedUrl(frameFile, presigned);

          const res = await reprocessReleaseFrames({
            releaseId,
            body: {
              frame_qty_file: fileUrl,
              frame_bom_mapping: selectedMappingId,
            },
          }).unwrap();

          toasterApi({
            type: 'success',
            status: 200,
            code: 'Success',
            message: `Frames re-imported (created ${res.summary.created}, updated ${res.summary.updated})`,
          });
        } catch (e: any) {
          toasterApi({
            type: 'error',
            status: e?.status || 500,
            code: 'Error',
            message:
              e?.data?.detail ||
              e?.data?.error ||
              e?.message ||
              'Failed to re-import frames',
          });
        }
      },
    });
  };

  return (
    <Card.Root width="100%" variant="elevated">
      <Spinner
        isLoading={isLoading || isPresigning}
        text="Re-importing frames..."
      />
      <Card.Body gap="5">
        <Box>
          <Heading size="sm" color="secondary.fg">
            Re-import Frame BOM
          </Heading>
          <Text fontSize="xs" color="fg.subtle">
            Select a saved Frame BOM mapping and upload a Frame BOM (frame
            quantity) file to (re)create frames for this release.
          </Text>
        </Box>

        {!frameBomMappings?.length ? (
          <Text fontSize="sm" color="fg.subtle">
            No Frame BOM mappings found. Create one in BOM Mapping
            {' \u2192 '}Frame BOM.
          </Text>
        ) : (
          <Box>
            <Text fontSize="sm" mb={2}>
              Frame BOM Mapping <Span color="fg.error">*</Span>
            </Text>
            <SelectWithAction
              items={frameBomMappings.map((m) => m.name)}
              value={selectedMapping?.name}
              placeholder="Choose a Frame BOM mapping"
              onSelect={(value: string) => {
                const match = frameBomMappings.find((m) => m.name === value);
                setSelectedMappingId(match?.id ?? null);
              }}
            />
          </Box>
        )}

        <Box>
          <Text fontSize="sm" mb={2}>
            Frame BOM File <Span color="fg.error">*</Span>
          </Text>
          <FileUpload
            fileType="excel"
            maxFiles={1}
            onUpload={(file: File | File[]) => {
              if (Array.isArray(file)) {
                setFrameFile(file[0]);
              } else {
                setFrameFile(file);
              }
            }}
          />
        </Box>

        <Button
          onClick={handleReimport}
          disabled={!canReimport}
          variant="outline"
          color="tertiary.fg"
          width="100%"
        >
          Re-import Frames
        </Button>
      </Card.Body>
    </Card.Root>
  );
};

const ManageCard: React.FC<Props> = ({
  defaultRelease,
  isNew = false,
  replacementFor,
}: Props) => {
  return (
    <Stack gap={5}>
      <Inner_ManageCard
        defaultRelease={defaultRelease}
        isNew={isNew}
        replacementFor={replacementFor}
      />
      {!isNew && defaultRelease?.id && (
        <ReimportFrameBomCard releaseId={defaultRelease.id} />
      )}
    </Stack>
  );
};

export default ManageCard;

export const releaseSchema = z.object({
  project: z.coerce.number().positive({ error: 'Project is required' }),
  number: z.string().nonempty({ error: 'Release number is required' }),
  bom_mapping: z.coerce.number().positive({ error: 'BOM mapping is required' }),
  bom_file: z.instanceof(File, { error: 'BOM file is required' }),
  frame_bom_mapping: z.coerce.number().positive().optional(),
  frame_qty_file: z.instanceof(File).optional(),
  due_date: z.string().optional(),
});

export const replacementReleaseSchema = releaseSchema.extend({
  replacement_reason: z
    .string()
    .trim()
    .min(1, { error: 'Replacement reason is required' }),
});

export const releaseSchemaPartial = z.object({
  project: z.coerce
    .number()
    .positive({ error: 'Project is required' })
    .optional(),
  number: z
    .string()
    .nonempty({ error: 'Release number is required' })
    .optional(),
  bom_mapping: z.coerce
    .number()
    .positive({ error: 'BOM mapping is required' })
    .optional(),
  bom_file: z.instanceof(File).optional(),
  frame_bom_mapping: z.coerce.number().positive().optional(),
  frame_qty_file: z.instanceof(File).optional(),
  due_date: z.string().optional(),
});